import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  addDoc,
  query,
  where,
  orderBy 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { createUserWithEmailAndPassword, getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { auth, firebaseConfig, initializeSecondaryApp, deleteApp } from '../config/firebase';
import { sendWelcomeEmail as sendCustomWelcomeEmail, sendCustomPasswordReset } from '../services/emailService';
import AddUserModal from '../components/users/AddUserModal';
import EditUserModal from '../components/users/EditUserModal';
import BulkUserImportModal from '../components/users/BulkUserImportModal';
import '../styles/UserManagement.css';

const UserManagement = () => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Check if current user is admin
  const isAdmin = currentUser?.role === 'Admin';

  useEffect(() => {
    if (!isAdmin) {
      return;
    }
    fetchUsers();
    fetchDistricts();
  }, [isAdmin]);

  const fetchUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, orderBy('email'));
      const querySnapshot = await getDocs(q);
      
      const usersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDistricts = async () => {
    try {
      const districtsRef = collection(db, 'districts');
      const querySnapshot = await getDocs(districtsRef);
      
      const districtsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setDistricts(districtsData);
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const handleAddUser = async (userData) => {
    try {
      // Check if the email already exists in Firestore first
      const usersRef = collection(db, 'users');
      const existingQuery = query(usersRef, where('email', '==', userData.email));
      const existingSnapshot = await getDocs(existingQuery);
      if (!existingSnapshot.empty) {
        throw new Error('A user with this email address already exists in the system.');
      }

      // Generate a temporary password for Firebase Auth creation
      const tempPassword = Math.random().toString(36).slice(-12) + 'A1!';

      // Use a secondary Firebase app instance so that creating the new Auth account
      // does NOT sign out the current admin user (createUserWithEmailAndPassword
      // would otherwise replace the active session with the newly created account,
      // causing the subsequent Firestore write to fail the isAdmin() rule).
      const secondaryApp = initializeSecondaryApp(firebaseConfig, `create-user-${Date.now()}`);
      const secondaryAuth = getAuth(secondaryApp);
      let newUid;
      try {
        const userCredential = await createUserWithEmailAndPassword(
          secondaryAuth,
          userData.email,
          tempPassword
        );
        newUid = userCredential.user.uid;
      } catch (authError) {
        if (authError.code === 'auth/email-already-in-use') {
          throw new Error(
            `This email is already registered in Firebase Authentication but has no matching Firestore record. ` +
            `To fix this, go to the Firebase Console → Authentication → Users, delete the account for "${userData.email}", then try again.`
          );
        }
        throw authError;
      } finally {
        await deleteApp(secondaryApp);
      }

      // Add user data to Firestore
      const userDocRef = await addDoc(collection(db, 'users'), {
        uid: newUid,
        email: userData.email,
        displayName: userData.displayName,
        role: userData.role,
        districtIds: userData.districtIds || [],
        districtId: userData.districtIds && userData.districtIds.length > 0 ? userData.districtIds[0] : null, // Legacy support
        active: true,
        needsPasswordReset: true,
        createdAt: new Date(),
        createdBy: currentUser.uid
      });

      // Send custom password reset email automatically
      try {
        await sendCustomPasswordReset(userData.email);
        console.log('Custom password reset email sent to:', userData.email);
      } catch (resetError) {
        console.error('Error sending custom password reset email:', resetError);
        // Fallback to Firebase default if custom email fails
        try {
          await sendPasswordResetEmail(auth, userData.email);
          console.log('Fallback: Firebase password reset email sent to:', userData.email);
        } catch (fallbackError) {
          console.error('Error sending fallback password reset email:', fallbackError);
        }
      }

      // Send welcome email if requested using custom email service
      if (userData.sendWelcomeEmail) {
        try {
          await sendCustomWelcomeEmail({
            email: userData.email,
            displayName: userData.displayName,
            role: userData.role
          });
          console.log('Custom welcome email sent successfully');
        } catch (emailError) {
          console.error('Error sending custom welcome email:', emailError);
          // Fall back to the old method if custom email fails
          await sendWelcomeEmail(userData, newUid);
        }
      }

      setShowAddModal(false);
      fetchUsers();
      
      // Show success message with instructions
      alert(`User created successfully! ${userData.sendWelcomeEmail ? 'A welcome email and password reset link have been sent to ' + userData.email + '.' : 'A password reset link has been sent to ' + userData.email + '. Please provide them with the site URL.'}`);
    } catch (error) {
      console.error('Error adding user:', error);
      throw error;
    }
  };

  const sendWelcomeEmail = async (userData, uid) => {
    try {
      // Get the current site URL
      const siteUrl = 'https://dcom.twkbom.org';
      
      // Create welcome email content
      const emailContent = {
        to: userData.email,
        subject: 'Welcome to Connectional - Account Setup Required',
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #2c3e50;">Welcome to Connectional</h2>
            
            <p>Hello ${userData.displayName},</p>
            
            <p>An account has been created for you in the Ministry CRM system. To get started, you'll need to set up your password.</p>
            
            <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
              <h3 style="color: #2c3e50; margin-top: 0;">Account Details:</h3>
              <p><strong>Email:</strong> ${userData.email}</p>
              <p><strong>Role:</strong> ${userData.role}</p>
              ${(userData.role === 'dCOM' || userData.role === 'dCOM Member') && userData.districtIds.length > 0 ? 
                `<p><strong>Districts:</strong> ${userData.districtIds.map(id => {
                  const district = districts.find(d => d.id === id);
                  return district ? district.name : 'Unknown';
                }).join(', ')}</p>` : ''
              }
            </div>
            
            <div style="background-color: #e8f4fd; padding: 20px; border-radius: 8px; margin: 20px 0;">
              <h3 style="color: #2c3e50; margin-top: 0;">Getting Started:</h3>
              <ol>
                <li>Visit the Ministry CRM website: <a href="${siteUrl}" style="color: #3498db;">${siteUrl}</a></li>
                <li>Click on "Forgot Password?" on the login page</li>
                <li>Enter your email address: ${userData.email}</li>
                <li>Check your email for password reset instructions</li>
                <li>Set your new password and log in</li>
              </ol>
            </div>
            
            <p>If you have any questions or need assistance, please contact your system administrator.</p>
            
            <p>Best regards,<br>Ministry CRM Team</p>
            
            <hr style="border: none; border-top: 1px solid #eee; margin: 30px 0;">
            <p style="font-size: 12px; color: #7f8c8d;">
              This is an automated message. Please do not reply to this email.
            </p>
          </div>
        `
      };

      // In a real implementation, you would send this email through your email service
      // For now, we'll log it and show the admin the email content
      console.log('Welcome email content:', emailContent);
      
      // You could integrate with services like:
      // - Firebase Functions with SendGrid
      // - AWS SES
      // - Nodemailer
      // - Any other email service
      
    } catch (error) {
      console.error('Error sending welcome email:', error);
      // Don't throw error here as user creation was successful
    }
  };

  const handleEditUser = async (userId, userData) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        ...userData,
        updatedAt: new Date(),
        updatedBy: currentUser.uid
      });

      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        active: !currentStatus,
        updatedAt: new Date(),
        updatedBy: currentUser.uid
      });

      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'users', userId));
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const getDistrictNames = (user) => {
    if (user.role !== 'dCOM' && user.role !== 'dCOM Member') return 'N/A';
    
    const userDistrictIds = user.districtIds || (user.districtId ? [user.districtId] : []);
    if (userDistrictIds.length === 0) return 'None assigned';
    
    const districtNames = userDistrictIds.map(id => {
      const district = districts.find(d => d.id === id);
      return district ? district.name : 'Unknown';
    });
    
    return districtNames.join(', ');
  };

  const filteredUsers = users.filter(user => {
    const matchesFilter = filter === 'all' || user.role === filter;
    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.displayName?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (!isAdmin) {
    return (
      <div className="user-management">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to access user management.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="user-management">
        <div className="loading">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h1>User Management</h1>
        <div className="header-actions">
          <button 
            className="btn btn-secondary"
            onClick={() => setShowBulkImportModal(true)}
          >
            Bulk Import Users
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            Add New User
          </button>
        </div>
      </div>

      <div className="user-management-filters">
        <div className="filter-group">
          <label>Filter by Role:</label>
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Roles</option>
            <option value="Admin">Admin</option>
            <option value="dCOM">dCOM</option>
            <option value="dCOM Member">dCOM Member</option>
            <option value="Candidate">Candidate</option>
          </select>
        </div>

        <div className="search-group">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Search by email or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>District</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id} className={!user.active ? 'inactive-user' : ''}>
                <td>{user.displayName || 'N/A'}</td>
                <td>{user.email}</td>
                <td>
                  <span className={`role-badge role-${user.role?.toLowerCase()}`}>
                    {user.role}
                  </span>
                </td>
                <td>{getDistrictNames(user)}</td>
                <td>
                  <span className={`status-badge ${user.active ? 'active' : 'inactive'}`}>
                    {user.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => setEditingUser(user)}
                    >
                      Edit
                    </button>
                    <button
                      className={`btn btn-sm ${user.active ? 'btn-warning' : 'btn-success'}`}
                      onClick={() => handleToggleUserStatus(user.id, user.active)}
                    >
                      {user.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteUser(user.id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredUsers.length === 0 && (
          <div className="no-users">
            <p>No users found matching your criteria.</p>
          </div>
        )}
      </div>

      {showAddModal && (
        <AddUserModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddUser}
          districts={districts}
        />
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSubmit={handleEditUser}
          districts={districts}
        />
      )}

      {showBulkImportModal && (
        <BulkUserImportModal
          onClose={() => setShowBulkImportModal(false)}
          onComplete={() => {
            setShowBulkImportModal(false);
            fetchUsers();
          }}
          districts={districts}
        />
      )}
    </div>
  );
};

export default UserManagement;
